// Peak level meters (dBFS) for every audio channel.
// The AudioWorklet reports the sample peak of each channel about 30 times per second;
// AudioMeters keeps the falling bars and peak holds and draws them on a canvas.

const PROCESSOR_NAME = "ks-peak-meter";

const PROCESSOR_CODE = `
class PeakMeterProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.block = Math.round(sampleRate / 30);
    this.count = 0;
    this.peaks = [];
  }
  process(inputs) {
    var input = inputs[0] || [];
    var n = input.length;
    if (this.peaks.length !== n) this.peaks = new Array(n).fill(0);
    var len = n ? input[0].length : 128;
    for (var c = 0; c < n; c++) {
      var x = input[c];
      var p = this.peaks[c];
      for (var i = 0; i < x.length; i++) {
        var v = x[i] < 0 ? -x[i] : x[i];
        if (v > p) p = v;
      }
      this.peaks[c] = p;
    }
    this.count += len;
    if (this.count >= this.block) {
      // No channels while the element has no audio: reported as an empty list
      this.port.postMessage(this.peaks);
      this.peaks = new Array(n).fill(0);
      this.count = 0;
    }
    return true;
  }
}

registerProcessor("${PROCESSOR_NAME}", PeakMeterProcessor);
`;

const loaded = new WeakMap();

// Creates the meter node for the context; the module is loaded once per context
export function createPeakMeter(ctx) {
	var promise = loaded.get(ctx);
	if (!promise) {
		var url = URL.createObjectURL(new Blob([PROCESSOR_CODE], {type: "application/javascript"}));
		promise = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
		loaded.set(ctx, promise);
	}
	return promise.then(() => new AudioWorkletNode(ctx, PROCESSOR_NAME, {numberOfOutputs: 1, outputChannelCount: [1]}));
}

const MIN_DB = -60;
const FALL_DB_PER_S = 24;
const HOLD_MS = 1500;
const SCALE = [0, -6, -12, -18, -24, -30, -40, -50, -60];
// Color zones from the bottom up: dark green, green, bright green, yellow and red (> -1 dB)
const ZONES = [
	{to: -36, lit: "#2f7d2f", unlit: "rgba(47, 125, 47, 0.4)"},
	{to: -18, lit: "#3fa63f", unlit: "rgba(63, 166, 63, 0.4)"},
	{to: -6, lit: "#72e03c", unlit: "rgba(114, 224, 60, 0.36)"},
	{to: -1, lit: "#f2e33a", unlit: "rgba(242, 227, 58, 0.36)"},
	{to: 0, lit: "#ec3b2b", unlit: "rgba(236, 59, 43, 0.42)"}
];

function zone_color(db) {
	var zone = ZONES.find(z => db <= z.to) || ZONES[ZONES.length - 1];
	return zone.lit;
}

// IEC 60268-18 meter deflection, 0 at -60 dB and 1 at 0 dB
function deflection(db) {
	var d;
	if (db < -70) d = 0;
	else if (db < -60) d = (db + 70) * 0.25;
	else if (db < -50) d = (db + 60) * 0.5 + 2.5;
	else if (db < -40) d = (db + 50) * 0.75 + 7.5;
	else if (db < -30) d = (db + 40) * 1.5 + 15;
	else if (db < -20) d = (db + 30) * 2 + 30;
	else if (db < 0) d = (db + 20) * 2.5 + 50;
	else d = 100;
	return Math.max(0, (d - 2.5) / 97.5);
}

function channel_labels(n) {
	if (n === 1) return ["M"];
	if (n === 2) return ["L", "R"];
	if (n === 6) return ["L", "R", "C", "LFE", "Ls", "Rs"];
	return Array.from({length: n}, (v, i) => String(i + 1));
}

export class AudioMeters {
	constructor (canvas) {
		this.canvas = canvas;
		this.levels = [];
		this.holds = [];
		// [{track, n}]: track 1 is the played audio, the others are extra tracks with decoded channels
		this.groups = [];
		this.groups_key = "";
		this.main_n = 0;
		this.last = 0;
		this.width = 0;
	}
	// peaks: linear sample peaks per channel; an empty list keeps the bars and lets them fall.
	// extra: optional peaks of other audio tracks, one list per track (tracks 2, 3, ...)
	add (peaks, now, extra) {
		var dt = this.last ? Math.min(0.5, (now - this.last) / 1000) : 0;
		this.last = now;
		// A paused element gives silence on a single channel; only real audio changes the channel count
		if (peaks.length !== this.main_n && peaks.length && (!this.main_n || peaks.some(p => p > 0))) {
			this.main_n = peaks.length;
		}
		var groups = this.main_n ? [{track: 1, n: this.main_n}] : [];
		var values = peaks.length === this.main_n ? Array.from(peaks) : new Array(this.main_n).fill(0);
		(extra || []).forEach((e, i) => {
			if (!e.length) return;
			groups.push({track: i + 2, n: e.length});
			values = values.concat(Array.from(e));
		});
		var key = groups.map(g => g.track + ":" + g.n).join(",");
		if (key !== this.groups_key) {
			this.groups_key = key;
			this.groups = groups;
			this.levels = new Array(values.length).fill(-Infinity);
			this.holds = values.map(() => ({db: -Infinity, time: 0}));
		}
		for (var c = 0; c < this.levels.length; c++) {
			var db = values[c] > 0 ? 20 * Math.log10(values[c]) : -Infinity;
			var fallen = this.levels[c] - FALL_DB_PER_S * dt;
			this.levels[c] = Math.max(db, fallen);
			var hold = this.holds[c];
			if (db >= hold.db || now - hold.time > HOLD_MS) {
				hold.db = db;
				hold.time = now;
			}
		}
	}
	// Draws the meters and returns the CSS width of the canvas, which depends on the
	// channel count and the canvas height only
	draw () {
		var canvas = this.canvas;
		var n = this.levels.length;
		var h = canvas.clientHeight;
		if (!n || !h) return 0;
		var font = Math.round(Math.min(10, Math.max(7, h * 0.026)));
		var show_scale = h >= 110;
		var bar = Math.round(Math.min(12, Math.max(4, h * 0.028)));
		var gap = Math.max(2, Math.round(bar * 0.5));
		var pad = Math.max(3, Math.round(font * 0.4));
		var pad_x = 1;
		var scale_w = show_scale ? Math.ceil(font * 1.9) + 4 : 0;
		var multi = this.groups.length > 1;
		// Track numbers get their own row above the channel labels
		var label_h = show_scale ? (font + 3) * (multi ? 2 : 1) : 0;
		var group_gap = gap + bar;
		var xs = [];
		var group_x = [];
		var x_pos = pad_x + scale_w;
		this.groups.forEach((group, gi) => {
			if (gi) x_pos += group_gap - gap;
			var start = x_pos;
			for (var i = 0; i < group.n; i++) {
				xs.push(x_pos);
				x_pos += bar + gap;
			}
			group_x.push({start: start, end: x_pos - gap});
		});
		var width = x_pos - gap + pad_x;
		if (width !== this.width) {
			this.width = width;
			canvas.style.width = width + "px";
		}
		var dpr = window.devicePixelRatio || 1;
		var pw = Math.round(width * dpr);
		var ph = Math.round(h * dpr);
		if (canvas.width !== pw || canvas.height !== ph) {
			canvas.width = pw;
			canvas.height = ph;
		}
		var g = canvas.getContext("2d");
		g.setTransform(dpr, 0, 0, dpr, 0, 0);
		g.clearRect(0, 0, width, h);

		var top = pad + label_h + (show_scale ? Math.ceil(font / 2) : 0);
		var bottom = h - pad - (show_scale ? Math.ceil(font / 2) : 0);
		var span = Math.max(1, bottom - top);
		var y_of = (db) => bottom - deflection(db) * span;
		var x0 = pad_x + scale_w;

		g.font = font + "px Roboto, sans-serif";
		g.textBaseline = "middle";
		if (show_scale) {
			// No background, so the texts get a dark shadow to stay readable on a bright picture
			g.save();
			g.shadowColor = "rgba(0, 0, 0, 0.9)";
			g.shadowBlur = 2;
			g.fillStyle = "rgba(255, 255, 255, 0.95)";
			g.textAlign = "right";
			SCALE.forEach((db) => {
				var y = Math.round(y_of(db)) + 0.5;
				g.fillText(String(db), x0 - 5, y);
				g.fillRect(x0 - 3, y - 0.5, 2, 1);
			});
			g.textAlign = "center";
			var row = font + 3;
			var c0 = 0;
			this.groups.forEach((group, gi) => {
				if (multi) {
					g.font = font + "px Roboto, sans-serif";
					g.fillText(String(group.track), (group_x[gi].start + group_x[gi].end) / 2, pad + row / 2);
				}
				channel_labels(group.n).forEach((label, i) => {
					g.font = (label.length > 2 ? Math.max(6, font - 2) : font) + "px Roboto, sans-serif";
					g.fillText(label, xs[c0 + i] + bar / 2, pad + label_h - row / 2);
				});
				c0 += group.n;
			});
			g.restore();
		}

		for (var c = 0; c < n; c++) {
			var x = xs[c];
			var level = this.levels[c];
			var lit_y = level > MIN_DB ? y_of(level) : bottom;
			// Every zone is drawn dim above the level and in full color below it
			var from_y = bottom;
			ZONES.forEach((zone) => {
				var to_y = y_of(zone.to);
				var split = Math.min(from_y, Math.max(to_y, lit_y));
				if (split > to_y) {
					g.fillStyle = zone.unlit;
					g.fillRect(x, to_y, bar, split - to_y);
				}
				if (from_y > split) {
					g.fillStyle = zone.lit;
					g.fillRect(x, split, bar, from_y - split);
				}
				from_y = to_y;
			});
			var hold = this.holds[c].db;
			if (hold > MIN_DB) {
				g.fillStyle = zone_color(hold);
				g.fillRect(x, Math.round(y_of(hold)) - 1, bar, 2);
			}
		}
		// Thin dividers over the bars like on the reference meter
		if (show_scale) {
			g.fillStyle = "rgba(0, 0, 0, 0.35)";
			SCALE.forEach((db) => {
				var y = Math.round(y_of(db));
				group_x.forEach((gx) => g.fillRect(gx.start, y, gx.end - gx.start, 1));
			});
		}
		return width;
	}
}
